import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Adb2cAuthService } from 'src/app/core/services/adb2c-auth.service';
import { AuthService } from 'src/app/shared/services/auth.service';
import { JwtHelperService } from '@auth0/angular-jwt';
import { CookieService } from 'ngx-cookie-service';
import { LocalStorageService } from 'src/app/core/services/local-storage.service';
import { localStorageConstants } from 'src/app/core/localStorageConstants/localStorageConstants';
import { switchMap, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { CommonService } from 'src/app/shared/services/common.service';

@Component({
  selector: 'app-auth-callback',
  templateUrl: './auth-callback.component.html',
  styleUrls: ['./auth-callback.component.scss']
})
export class AuthCallbackComponent implements OnInit {
  isProcessing = true;
  error = false;
  message = 'Processing authentication...';
  subMessage = 'Please wait while we complete your login.';
  errorMessage = '';
  tenant: any;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private adb2cAuthService: Adb2cAuthService,
    private authService: AuthService,
    private cookieService: CookieService,
    private localStorageService: LocalStorageService,
    private commonService: CommonService,
  ) { }

  ngOnInit(): void {
    // Extract authorization code from query parameters
    const code = this.route.snapshot.queryParamMap.get('code');
    const state = this.route.snapshot.queryParamMap.get('state');
    const error = this.route.snapshot.queryParamMap.get('error');

    if (error) {
      const errorDescription = this.route.snapshot.queryParamMap.get('error_description');

      this.handleAuthError(error, errorDescription);
      return;
    }

    if (code) {
      // Validate state parameter if present
      if (state) {
        const storedState = sessionStorage.getItem('auth_state');
        if (storedState && storedState !== state) {
          this.handleAuthError('invalid_state', 'State parameter validation failed');
          return;
        }
        // Clean up stored state
        sessionStorage.removeItem('auth_state');
      }

      // Directly call the token exchange service
      this.exchangeCodeForToken(code);
    } else {
      this.handleAuthError('no_code', 'No authorization code received from Azure AD B2C');
    }
  }

  /**
   * Exchange authorization code for access token using the existing service
   * @param code - Authorization code from Azure AD B2C
   */
  private exchangeCodeForToken(code: string): void {
    this.message = 'Redirecting you to P-CIS...';
    this.subMessage = 'Please wait while we complete your login.';

    // Check if this is a magic link callback by examining the state or session storage
    const isMagicLink = this.isMagicLinkCallback();

    // Choose the appropriate service method based on the authentication flow
    const tokenExchangeObservable = isMagicLink
      ? this.adb2cAuthService.getMagicLinkTokenFromCode(code)
      : this.adb2cAuthService.getAuthTokenFromCode(code);

    // Use the selected service to call the appropriate API endpoint
    tokenExchangeObservable
      .pipe(
        switchMap((response: any) => {
          // Check if the response is successful
          if (response && response.StatusCode === 200 && response.Data) {
            const tokenData = response.Data;

            // Process the tokens
            if (tokenData.accessToken) {
              this.message = 'Authentication successful!';
              this.subMessage = 'Setting up your session...';

              this.processTokenAndSetCookies(tokenData.accessToken);

              // Store refresh token if available
              if (tokenData.refreshToken) {
                localStorage.setItem('refreshToken', tokenData.refreshToken);
              }

              // Check for deep link scenario before redirecting
              const storedRedirectUrl = sessionStorage.getItem('redirectUrl');
              const storedPath = sessionStorage.getItem('path');

              if (storedRedirectUrl) {
                this.message = 'Login successful!';
                this.subMessage = 'Redirecting to your requested page...';
                this.isProcessing = false;

                // Clean up session storage before redirect
                this.cleanupSessionStorage();

                // Small delay to show success message before redirect
                setTimeout(() => {
                  this.message = 'Redirecting to your page...';
                  this.commonService?.safeRedirect(storedRedirectUrl);
                }, 1000);
              } else if (storedPath) {
                this.message = 'Login successful!';
                this.subMessage = 'Redirecting to your requested page...';
                this.isProcessing = false;

                // Clean up session storage before redirect
                this.cleanupSessionStorage();

                // Small delay to show success message before redirect
                setTimeout(() => {
                  this.message = 'Redirecting to your page...';
                  this.commonService?.safeRedirect(storedPath.toString());
                }, 1000);
              } else {
                this.redirectToDashboard();
              }

              return of(tokenData);
            } else {
              throw new Error('No access token received from service');
            }
          } else {
            throw new Error(`Service returned error: ${response?.Message || 'Unknown error'}`);
          }
        }),
        catchError(error => {
          this.handleAuthError('token_exchange_failed', `Failed to exchange authorization code: ${error.message}`);
          return of(null);
        })
      )
      .subscribe(response => {
        // Token exchange completed
      });
  }

  /**
   * Determines if the current callback is from a magic link flow
   * @returns true if this is a magic link callback, false otherwise
   */
  private isMagicLinkCallback(): boolean {
    // Check multiple indicators to determine if this is a magic link callback

    // 1. Check session storage for magic link indicators
    const tokenHintProcessing = sessionStorage.getItem('token_hint_processing');
    const tokenHint = sessionStorage.getItem('token_hint');

    if (tokenHintProcessing || tokenHint) {
      return true;
    }

    // 2. Check the redirect URI - magic links typically use /auth/callback
    const currentPath = window.location.pathname;
    if (currentPath.includes('/auth/callback')) {
      return true;
    }

    // 3. Check the state parameter for magic link indicators
    const state = this.route.snapshot.queryParamMap.get('state');
    if (state) {
      const storedState = sessionStorage.getItem('auth_state');
      // If we have a stored state and it was set during magic link flow
      if (storedState && sessionStorage.getItem('magic_link_flow')) {
        return true;
      }
    }

    // 4. Check if the current URL contains magic link specific parameters
    const urlParams = new URLSearchParams(window.location.search);
    const hasIdTokenHint = urlParams.has('id_token_hint') ||
                          sessionStorage.getItem('id_token_hint_used') === 'true';

    if (hasIdTokenHint) {
      return true;
    }

    return false;
  }

  /**
   * Process the access token and set cookies/local storage
   * Based on the logic from adb2c-refresh-token.component.ts
   * @param accessToken - The access token received from authentication
   */
  private processTokenAndSetCookies(accessToken: string): void {
    const helper = new JwtHelperService();
    const decodedToken = helper.decodeToken(accessToken);

    if (!decodedToken) {
      this.handleAuthError('invalid_token', 'Invalid access token received');
      return;
    }

    // Check if user has API User role (not allowed)
    if (decodedToken.extension_role === 'API User') {
      this.handleAuthError('unauthorized_role', 'API User role is not allowed for web login');
      return;
    }

    const hostname = window.location.origin;

    // Check if the token is for the correct instance
    if (!decodedToken.extension_instanceURL.includes(hostname) && !/^.*localhost.*/.test(hostname)) {
      this.commonService.safeRedirect(decodedToken.extension_instanceURL + '?token=' + accessToken);
      return;
    }

    // Set authentication data
    this.authService.setLocalStorage(accessToken);
    sessionStorage.setItem('isFirstLogin', 'true');

    // Check for user switching
    if (this.cookieService.get('email') !== decodedToken.email) {
      const isSecure = window.location.protocol === 'https:';
      this.cookieService.set('switchUser', 'true', undefined, '/', undefined, isSecure, 'Strict');
      this.cookieService.deleteAll('');
      this.cookieService.deleteAll('/');
      this.localStorageService.deleteLocalStorageByKey(localStorageConstants.userDetails);
      // Dedicated cross-tab signal other tabs' 'storage' listener reacts to (PCIS-11524) - narrower
      // than reacting to every localStorage write, which self-triggers a cross-tab reload cascade.
      localStorage.setItem(localStorageConstants.userSwitchSignal, Date.now().toString());
    }

    // Set tenant for permissions check
    this.tenant = decodedToken.extension_tenantAbbreviation;
  }

  /**
   * Clean up session storage after successful authentication
   */
  private cleanupSessionStorage(): void {
    sessionStorage.removeItem('redirectUrl');
    sessionStorage.removeItem('returnUrl');
    sessionStorage.removeItem('token_hint');
    sessionStorage.removeItem('token_hint_processing');
    sessionStorage.removeItem('auth_code');
    sessionStorage.removeItem('auth_state_received');
    sessionStorage.removeItem('auth_state');
    sessionStorage.removeItem('magic_link_flow');
    sessionStorage.removeItem('id_token_hint_used');
  }

  /**
   * Redirect to dashboard with proper error handling
   */
  private redirectToDashboard(): void {
    this.message = 'Login successful!';
    this.subMessage = 'Redirecting to dashboard....';
    this.isProcessing = false;

    // Clean up session storage
    this.cleanupSessionStorage();

    // Small delay to show success message before redirect
    setTimeout(() => {
      this.message = 'Redirecting to dashboard...';

      // Use Angular router navigation instead of direct redirect
      this.router.navigate(['/dashboard']).then(
        (success) => {
          if (!success) {
            // Force page reload as fallback
            window.location.href = '/dashboard';
          }
        }
      ).catch(error => {
        window.location.href = '/dashboard';
      });
    }, 1000);
  }

  /**
   * Handle authentication errors
   * @param error - Error code
   * @param description - Error description
   */
  private handleAuthError(error: string, description?: string): void {
    this.isProcessing = false;
    this.error = true;
    this.message = 'Authentication failed';
    this.errorMessage = description || error;

    // Redirect to login page after a delay
    setTimeout(() => {
      this.router.navigate(['/login'], {
        queryParams: { error: error, error_description: description }
      });
    }, 3000);
  }
}
